import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { clone, getElementById } from "../util/Utils";
import type { Predicate } from "../types/Predicate";
import { extendLabel, getStatus, updateLabel, updateStatus } from "../tracking/FeatureTracking";
import type { FeatureStatus } from "../tracking/Tracking";
import type { TrackingLabels } from "../tracking/TrackingLabels";

const FACET_VALUE_CLASS = `hc_facetValueInput`;
const FACET_VALUE_TITLE_CLASS = `hc_label__title`;
const FACET_VALUE_TITLE_SELECTOR = `.${FACET_VALUE_TITLE_CLASS}`;
const EXPANDED_CLASS = `hc_facetValueInput--expanded`;

export class SvelteFacetValue {
  static readonly factory = HeurekaElementFactory.byClass(FACET_VALUE_CLASS, SvelteFacetValue);
  readonly label: HTMLLabelElement | null;
  readonly span: HTMLSpanElement | null;

  /*               */
  constructor(
    readonly facetValue: HTMLInputElement,
    readonly liElem: HTMLLIElement | null = facetValue.closest("li"),
  ) {
    this.label = facetValue.closest("label");
    this.span = liElem?.querySelector(FACET_VALUE_TITLE_SELECTOR);
  }

  /*                  */
  /*               */
  /*                       */

  get id(): string | undefined {
    return this.facetValue.id;
  }

  get name(): string | undefined {
    return this.facetValue.name;
  }

  get checked(): boolean {
    return this.facetValue.checked;
  }

  get initiallyChecked(): boolean {
    if (this.facetValue.dataset.defaultChecked !== undefined) {
      return this.facetValue.dataset.defaultChecked === "true";
    }
    return this.facetValue.defaultChecked;
  }

  get nba(): boolean {
    return this.facetValue.dataset.nba === "true";
  }

  get popular(): boolean {
    return this.facetValue.dataset.popular === "true";
  }

  get ref(): string | undefined {
    return this.facetValue.dataset.tsFeatureRef;
  }

  get referencedElement(): HTMLElement | undefined {
    return getElementById(this.ref);
  }

  get title(): string | undefined {
    return this.span?.textContent || undefined;
  }

  set title(title: string | null | undefined) {
    if (this.span && title) {
      this.span.innerHTML = title;
    }
  }

  get hidden(): boolean {
    return this.liElem?.hidden || false;
  }

  set hidden(isHidden: boolean) {
    if (this.liElem) {
      this.liElem.hidden = isHidden;
    }
  }

  get expanded(): boolean {
    return this.facetValue.classList.contains(EXPANDED_CLASS);
  }

  set expanded(isExpanded: boolean) {
    this.facetValue.classList.toggle(EXPANDED_CLASS, isExpanded);
  }

  get offsetTop(): number {
    return this.liElem?.offsetTop || 0;
  }

  clone(): SvelteFacetValue {
    return SvelteFacetValue.factory.pick(undefined, clone(this.liElem));
  }

  cloneWithData(id: string, title: string): SvelteFacetValue {
    const svelteFacetValue = this.clone();
    svelteFacetValue.facetValue.id = `${id}--cloned`;
    svelteFacetValue.facetValue.dataset.tsFeatureRef = id;
    svelteFacetValue.title = title;
    return svelteFacetValue;
  }

  updateStatus(statusToUpdate: FeatureStatus, updatedStatus: FeatureStatus) {
    updateStatus(this.facetValue, updatedStatus, undefined, (oldStatus, newStatus) => {
      return oldStatus === statusToUpdate ? newStatus : oldStatus;
    });
  }

  /*                                                       */
  updateTrackingLabelsOnReferenced(
    status: FeatureStatus,
    filterMethod: TrackingLabels["san_FilterMethod"],
    facetLocation?: TrackingLabels["san_FacetLocation"],
  ) {
    if (this.referencedElement) {
      updateStatus(this.referencedElement, status, 2);
      updateLabel(this.facetValue, "san_FilterMethod", filterMethod);
      updateLabel(this.referencedElement, "san_FilterMethod", filterMethod);
      /*                                                             */
      /*                                                                    */
      if (facetLocation) {
        updateLabel(this.facetValue, "san_FacetLocation", facetLocation);
        updateLabel(this.referencedElement, "san_FacetLocation", facetLocation);
        /*                                                                     */
        /*                                                                                                        */
        const allReferencingElements = document.querySelectorAll(`[data-ts-feature-ref="${this.ref}"]`);
        allReferencingElements.forEach((elem) => {
          updateLabel(elem as HTMLElement, "san_FacetLocation", facetLocation);
        });
      }
    }
    return this;
  }

  toggleChecked(state: boolean) {
    if (this.facetValue) {
      this.facetValue.checked = state;
      this.facetValue.defaultChecked = state;
      this.facetValue.dataset.defaultChecked = state ? "true" : "false";
      if (state) {
        updateStatus(this.facetValue, "selected", 2);
      } else {
        updateStatus(this.facetValue);
      }
    }
  }

  get spanInnerHTML(): string | null {
    return this.span?.innerHTML;
  }
}

export const isSelected: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => value.checked;

export const isNotSelected: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => !value.checked;

export const isNba: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => value.nba;
export const isNotNba: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => !value.nba;
export const isPopular: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => value.popular;

export const updateFacetValueTagPosition = (value: SvelteFacetValue, index: number) =>
  value.referencedElement && updateLabel(value.referencedElement, "san_FacetValueTagPosition", (index + 1).toString());

export const updateNBAPosition = (value: SvelteFacetValue, index: number) =>
  value.referencedElement && updateLabel(value.referencedElement, "san_NBAPosition", (index + 1).toString());

export const updateNBARow = (value: SvelteFacetValue, rowIndex: number) =>
  value.referencedElement && updateLabel(value.referencedElement, "san_NBARow", rowIndex);
export const updateSanTopFacetValueWithNba = (value: SvelteFacetValue) =>
  value.referencedElement && extendLabel(value.referencedElement, "san_TopFacetValue", "nba");

export const setFeatureStatus = (value: SvelteFacetValue, status: FeatureStatus) => {
  const referencedElement = value.referencedElement;
  if (!referencedElement) return;
  const currentStatus = getStatus(referencedElement);
  if (!currentStatus || currentStatus === "loaded" || currentStatus == "visible") {
    updateStatus(referencedElement, status);
  }
};
